import React, { useState } from 'react'

import {
  Box,
  Grid,
  Skeleton,
  Table,
  TableCell,
  TableBody,
  TableRow,
  Typography,
} from '@mui/material'
import { useRequest } from 'ahooks'

import EnterpriseReportBox from '$adminSrc/components/EnterprisePage/Enterprise/EnterpriseReportBox'
import ImpactSection from '$adminSrc/components/EnterprisePage/Enterprise/ImpactSection'
import SignupsSection from '$adminSrc/components/EnterprisePage/Enterprise/SignupsSection'
import UsageSection from '$adminSrc/components/EnterprisePage/Enterprise/UsageSection'
import Toast from '$extensionSrc/components/Toast'
import { formatMonthDayAndYear } from '$extensionSrc/utils/dateUtils'

import { getEnterprisePlanReportPath } from '../urlConsts'
import styles from './EnterpriseReport.module.scss'
import MaterialUiProvider from './MaterialUiProvider'

// Helper component for section headers
function SectionHeader({ title, shaded }: { title: string; shaded?: boolean }) {
  return (
    <h2 className={`${styles.sectionHeader} ${shaded ? styles.shaded : ''}`}>
      {title}
    </h2>
  )
}

export default function EnterpriseReport({
  enterprisePlanId,
  cspNonce,
}: {
  enterprisePlanId: number | null
  cspNonce: string
}) {
  const [currentPlan, setCurrentPlan] = useState<{ [key: string]: any } | null>(
    null,
  )
  const [error, setError] = useState<string>('')

  useRequest(
    () => {
      const formData = new FormData()
      if (enterprisePlanId) {
        formData.append('enterprise_plan_id', enterprisePlanId.toString())
      }

      const request = new Request(getEnterprisePlanReportPath, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-TOKEN': window.csrfToken || '',
        },
      })

      return fetch(request)
    },
    {
      debounceWait: 300,
      onSuccess: (response: Response) => {
        response.json().then((data) => {
          if (response.status !== 200) {
            setError('Could not load enterprise report')
          } else if (data.error) {
            setError(data.error)
          } else {
            setCurrentPlan(data)
          }
        })
      },
      onError: () => {
        setError('Could not load enterprise report')
      },
    },
  )

  return (
    <MaterialUiProvider cspNonce={cspNonce}>
      {error && <Toast text={error} noAutoDismiss color="red" />}
      <Box className={styles.container}>
        {/* Header */}
        <Box className={styles.header}>
          <Grid alignItems="center" container gap={1.5}>
            <Grid>
              {currentPlan === null && (
                <Skeleton sx={{ minWidth: 200, minHeight: 40 }} />
              )}
              {currentPlan && (
                <Typography
                  component="div"
                  variant="h4"
                  className={styles.orgName}
                >
                  {currentPlan.enterprisePlan.metadata.orgName}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <SectionHeader title="Overview" shaded />
        <Box className={styles.section}>
          <EnterpriseReportBox
            content={
              <Table className={styles.overviewTable}>
                <TableBody>
                  <TableRow>
                    <TableCell>Seats in use</TableCell>
                    <TableCell>
                      {currentPlan === null ? (
                        <Skeleton width={150} />
                      ) : (
                        currentPlan?.enterprisePlan.metadata.inUseSeats
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Plan Renewal date</TableCell>
                    <TableCell>
                      {currentPlan === null ? (
                        <Skeleton width={150} />
                      ) : (
                        formatMonthDayAndYear(
                          currentPlan?.enterprisePlan.metadata
                            .planExpirationDate,
                        )
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            }
          />
        </Box>
        <Box className={styles.section}>
          <SignupsSection
            loading={currentPlan === null}
            totalSignups={currentPlan?.enterprisePlan.userSignups.total}
            userSignups={currentPlan?.enterprisePlan.userSignups.userSignups}
          />
        </Box>

        <Box className={styles.section}>
          <SectionHeader title="Usage" shaded />

          <UsageSection
            loading={currentPlan === null}
            checklistsUsed={currentPlan?.enterprisePlan.usage.checklistsUsed}
            recommendationsUpdated={
              currentPlan?.enterprisePlan.usage.recommendationsUpdated
            }
            contentDeleted={currentPlan?.enterprisePlan.usage.contentDeleted}
            connectionsRemoved={
              currentPlan?.enterprisePlan.usage.connectionsRemoved
            }
          />
        </Box>

        <Box className={styles.section}>
          <SectionHeader title="Impact" shaded />

          <ImpactSection
            loading={currentPlan === null}
            numUsersPerBucket={
              currentPlan?.enterprisePlan.impact.protectionScore
                .numUsersPerBucket
            }
            numUsersPerBucketPerConcernArea={
              currentPlan?.enterprisePlan.impact.protectionScore
                .numUsersPerBucketPerConcernArea
            }
            timeSaved={currentPlan?.enterprisePlan.impact.timeSaved}
          />
        </Box>
      </Box>
    </MaterialUiProvider>
  )
}
